import { Link } from 'react-router-dom'
import { CircleCheck } from 'lucide-react'
import type { ShiftAttentionItem } from '@/api/shiftDashboardApi'
import { showingLabel } from '@/lib/shiftTiles'
import { cn } from '@/lib/utils'
import { StatusChip } from './ShiftTiles'

/**
 * "Attention this shift" (z8nrz7fbcp): the highest-priority exceptions across
 * every tile, worst first, ranked by the server. Read-only — opening an item
 * goes to its record; nothing here releases or completes anything.
 */
export function AttentionPanel({
  items,
  total,
  loading,
  onOpenTile,
}: {
  items: ShiftAttentionItem[]
  total: number
  loading: boolean
  /** Opens the tile an item came from, for one without a record link. */
  onOpenTile: (tileId: string) => void
}) {
  const showing = showingLabel(items.length, total)
  const critical = items.filter((i) => i.severity === 'critical').length

  return (
    <section aria-labelledby='attention-heading' className='rounded-xl border border-gray-200 bg-white shadow-sm'>
      <header className='flex items-baseline justify-between gap-3 border-b px-4 py-3'>
        <h2 id='attention-heading' className='text-sm font-semibold text-foreground'>
          Attention this shift
        </h2>
        {!loading && total > 0 && (
          <span className='text-xs text-muted-foreground'>
            {critical > 0 ? `${critical} critical of ${total}` : `${total} to look at`}
          </span>
        )}
      </header>

      {loading ? (
        <div className='space-y-2 p-4'>
          {[0, 1, 2].map((i) => (
            <div key={i} className='h-14 animate-pulse rounded-lg bg-muted motion-reduce:animate-none' />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className='flex items-center gap-3 px-4 py-5'>
          <CircleCheck aria-hidden className='h-5 w-5 shrink-0 text-green-600' />
          <p className='text-sm text-foreground'>Nothing needs attention this shift.</p>
        </div>
      ) : (
        // Its own scroll area: a long list never stretches the page, and the
        // header stays in view while it scrolls.
        <ul className='max-h-[26rem] overflow-y-auto overscroll-contain'>
          {items.map((item) => (
            <li
              key={item.id}
              className={cn(
                'border-b border-l-2 last:border-b-0',
                item.severity === 'critical' ? 'border-l-red-600' : 'border-l-amber-500',
              )}
            >
              <AttentionRow item={item} onOpenTile={onOpenTile} />
            </li>
          ))}
        </ul>
      )}

      {showing && <p className='border-t px-4 py-2 text-xs text-muted-foreground'>{showing}</p>}
    </section>
  )
}

/**
 * One exception, compact: the badge and title on one line, then what and who
 * on one line. The whole row is the link, so the target is easy to hit.
 */
function AttentionRow({ item, onOpenTile }: { item: ShiftAttentionItem; onOpenTile: (tileId: string) => void }) {
  const body = (
    <>
      <span className='flex min-w-0 items-center gap-2'>
        <StatusChip status={item.severity} label={item.badge} />
        <span className='truncate text-sm font-semibold text-foreground'>{item.title}</span>
      </span>
      <span className='mt-1 flex min-w-0 items-baseline justify-between gap-3 text-xs text-muted-foreground'>
        <span className='truncate' title={item.subtitle}>
          {item.subtitle}
        </span>
        {item.owner ? (
          <span className='shrink-0 text-foreground/80'>{item.owner}</span>
        ) : (
          <span className='shrink-0 font-semibold text-red-700'>Unassigned</span>
        )}
      </span>
    </>
  )
  const cls =
    'flex w-full flex-col px-4 py-2.5 text-left hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none'
  return item.href ? (
    <Link to={item.href} className={cls}>
      {body}
    </Link>
  ) : (
    <button type='button' onClick={() => onOpenTile(item.tileId)} className={cls}>
      {body}
    </button>
  )
}
